/* eslint-disable @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-argument */
import React, { ChangeEvent, FC, useMemo, useState } from "react";
import Api from "../../utils/Api";

import DeviceStatusStore from "../../stores/DeviceStatusStore";
import SessionStore from "../../stores/SessionStore";

import ConfigStore from "../../stores/ConfigStore";
import ConfigActions from "../../actions/ConfigActions";
import DeviceStatusActions from "../../actions/DeviceStatusActions";

import "../../../css/ManageDeviceUpdates.css";

import { branchesMapToOptions, timesMapToOptions } from "../DeviceCreationConst";
import { useMount, useUnmount } from "react-use";
import { Box, Button, Checkbox, Flex, Input, SingleSearchSelector, Spinner, TextArea, useTranslation } from "@familyzone/component-library";
import CardBasedPage from "../templates/CardBasedPage";
import { SectionHeading } from "../shared/SectionHeading";
import { FormControl, FormLabel } from "@chakra-ui/form-control";

interface StatusResult {
  status: number;
  log: string;
}

interface VersionResult {
  uptime: number;
  version: string;
}

const ManageDeviceUpdates: FC = () => {
  const { t } = useTranslation();

  const [updateAllowAuto, setUpdateAllowAuto] = useState(false);
  const [updateBranch, setUpdateBranch] = useState("");
  const [updateStartTime, setUpdateStartTime] = useState("");
  const [updateEndTime, setUpdateEndTime] = useState("");
  const [currentVersion, setCurrentVersion] = useState("");

  const [newestVersion, setNewestVersion] = useState("");
  const [updateStatus, setUpdateStatus] = useState<number | undefined>(undefined);
  const [restartStatus, setRestartStatus] = useState(0);
  const [updateLog, setUpdateLog] = useState("");

  const [polling, setPolling] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [checkVersionTimeout, setCheckVersionTimeout] = useState<NodeJS.Timeout | null>(null);
  const [checkStatusTimeout, setCheckStatusTimeout] = useState<NodeJS.Timeout | null>(null);

  const [checkingVersion, setCheckingVersion] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState(false);

  useMount(() => {
    DeviceStatusStore.listen(onChange);
    SessionStore.listen(onChange);
    ConfigStore.listen(configChange);
    setTimeout(() => {
      ConfigActions.fetch();
    }, 0);
    checkStatus();
    checkVersion();
  });

  const checkVersion = () => {
    setCheckVersionTimeout(null);

    if (!checkingVersion) {
      setCheckingVersion(true);
      Api.get_with_timeout(
        "/managedevice/ajax/device/update/version",
        (result: VersionResult) => {
          setCurrentVersion(result.version);
          setCheckingVersion(false);
          if (polling) {
            setCheckVersionTimeout(setTimeout(checkVersion, 1000));
          }
        },
        () => {
          setCheckingVersion(false);
          setCheckVersionTimeout(setTimeout(checkVersion, 5000));
        },
        10000
      );
    }
  };

  const checkStatus = () => {
    setCheckStatusTimeout(null);

    if (!checkingStatus) {
      setCheckingStatus(true);
      Api.get_with_timeout(
        "/managedevice/ajax/device/update/status",
        (result: StatusResult) => {
          setCheckingStatus(false);
          if (!restarting && restartStatus === 1) {
            setRestartStatus(0);
          }
          if (!updating && result.status !== updateStatus) {
            setUpdateStatus(result.status);
            setPolling(false);
            checkVersion();
            DeviceStatusActions.fetch();
          }
          if (result.log && result.log.length !== updateLog.length) {
            setUpdateLog(result.log);
          }
          if (polling || result.status === 1) {
            setCheckStatusTimeout(setTimeout(checkStatus, 1000));
          }
        },
        () => {
          setCheckingStatus(false);
          setRestartStatus(1);
          setCheckStatusTimeout(setTimeout(checkStatus, 5000));
        },
        10000
      );
    }
  };

  useUnmount(() => {
    setPolling(false);
    DeviceStatusStore.unlisten(onChange);
    SessionStore.unlisten(onChange);
    if (checkStatusTimeout) {
      clearTimeout(checkStatusTimeout);
    }
    if (checkVersionTimeout) {
      clearTimeout(checkVersionTimeout);
    }
  });

  const onChange = () => {
    setNewestVersion(DeviceStatusStore.getNewestVersion() ? DeviceStatusStore.getNewestVersion() : "");
  };

  const configChange = () => {
    setUpdateAllowAuto(ConfigStore.getItemInConfig("update_allow_auto"));
    setUpdateBranch(ConfigStore.getItemInConfig("update_branch"));
    setUpdateStartTime(ConfigStore.getItemInConfig("update_start_time"));
    setUpdateEndTime(ConfigStore.getItemInConfig("update_end_time"));
  };

  const handleSubmit = () => {
    const settings = {
      update_allow_auto: updateAllowAuto,
      update_branch: updateBranch,
      update_start_time: updateStartTime,
      update_end_time: updateEndTime,
      update_arch: "amd64", // This setting is now fixed to the default
    };

    setSaving(true);
    setChanges(false);

    Api.post("/managedevice/ajax/device", settings, () => {
      ConfigActions.fetch();
      setSaving(false);
    });
  };

  const handleChangeUpdateEnable = (event: ChangeEvent<HTMLInputElement>) => {
    setUpdateAllowAuto(event.target.checked);
    setChanges(true);
  };

  const handleChangeUpdateBranch = (input: string) => {
    setUpdateBranch(input);
    setChanges(true);
  };

  const handleChangeUpdateStartTime = (input: string) => {
    setUpdateStartTime(input);
    setChanges(true);
  };

  const handleChangeUpdateEndTime = (input: string) => {
    setUpdateEndTime(input);
    setChanges(true);
  };

  const handleTriggerUpdate = () => {
    if (updateStatus !== 1) {
      setUpdateStatus(1);
      setPolling(true);
      setUpdating(true);
      Api.get("/managedevice/ajax/device/update/start", () => {
        setUpdating(false);
        checkStatus();
      });
    }
  };

  const handleTriggerRestart = () => {
    if (restartStatus !== 1) {
      setRestartStatus(1);
      setPolling(true);
      setRestarting(true);
      Api.get("/managedevice/ajax/device/restart", () => {
        setRestarting(false);
        checkStatus();
      });
    }
  };

  const breadcrumbs = [
    { title: t("Debugging"), url: "/managedevice", isActive: false },
    { title: t("Device Updates"), isActive: true },
  ];

  const selectedBranchOption = useMemo(() => branchesMapToOptions.find((item) => item.value === updateBranch), [updateBranch]);
  const selectedStartTimeOption = useMemo(() => timesMapToOptions.find((item) => item.value === updateStartTime), [updateStartTime]);
  const selectedEndTimeOption = useMemo(() => timesMapToOptions.find((item) => item.value === updateEndTime), [updateEndTime]);

  return (
    <CardBasedPage title={t("Device Updates")} breadcrumbs={breadcrumbs}>
      <Box p="sp24">
        <SectionHeading title={t("Automatic")} aria-level={2} />

        <Flex px="sp12" py="sp16" direction="column" gap="sp16">
          <FormControl mb="10px" isDisabled={saving}>
            <Checkbox
              isChecked={updateAllowAuto}
              onChange={handleChangeUpdateEnable}
              aria-label={`automatic update ${updateAllowAuto ? "enabled" : "disabled"}`}
            >
              {t("Enabled")}
            </Checkbox>
          </FormControl>

          <FormControl mb="10px" isDisabled={saving}>
            <Flex gap="sp8">
              <FormLabel width="110px" mt="sp12">
                {t("Branch")}
              </FormLabel>
              <Flex direction="column" width="80%">
                <SingleSearchSelector
                  options={branchesMapToOptions}
                  selected={selectedBranchOption}
                  onChange={(item) => {
                    if (item && item.value) handleChangeUpdateBranch(`${item.value}`);
                  }}
                  filterOptions={false}
                  showClearIcon={false}
                  disabled={saving}
                  placeholder={t("Select Branch")}
                  name="branch-selector"
                  aria-label={`selected branch${
                    selectedBranchOption && selectedBranchOption.label ? ` ${selectedBranchOption.label}` : ""
                  }`}
                />
              </Flex>
            </Flex>
          </FormControl>

          <FormControl mb="10px" isDisabled={saving}>
            <Flex gap="sp8">
              <FormLabel width="110px" mt="sp12">
                {t("Window")}
              </FormLabel>
              <Flex direction="column">
                <FormLabel fontSize="sm">{t("Start Time")}</FormLabel>
                <SingleSearchSelector
                  options={timesMapToOptions}
                  selected={selectedStartTimeOption}
                  onChange={(item) => {
                    if (item && item.value) handleChangeUpdateStartTime(`${item.value}`);
                  }}
                  filterOptions={false}
                  showClearIcon={false}
                  disabled={saving}
                  placeholder={t("Select Start Time")}
                  name="start-time-selector"
                  aria-label={`selected update window start time${
                    selectedStartTimeOption && selectedStartTimeOption.label ? ` ${selectedStartTimeOption.label}` : ""
                  }`}
                />
              </Flex>

              <Flex direction="column">
                <FormLabel fontSize="sm">{t("End Time")}</FormLabel>
                <SingleSearchSelector
                  options={timesMapToOptions}
                  selected={selectedEndTimeOption}
                  onChange={(item) => {
                    if (item && item.value) handleChangeUpdateEndTime(`${item.value}`);
                  }}
                  filterOptions={false}
                  showClearIcon={false}
                  disabled={saving}
                  placeholder={t("Select End Time")}
                  name="end-time-selector"
                  aria-label={`selected update window end time${
                    selectedEndTimeOption && selectedEndTimeOption.label ? ` ${selectedEndTimeOption.label}` : ""
                  }`}
                />
              </Flex>
            </Flex>
          </FormControl>

          <FormControl mb="10px" isDisabled>
            <Flex gap="sp8">
              <FormLabel width="110px" mt="sp12">
                {t("Active Version")}
              </FormLabel>
              <Flex direction="column" width="80%">
                <Input placeholder={t("Active version...")} value={currentVersion} isDisabled />
              </Flex>
            </Flex>
          </FormControl>
        </Flex>
      </Box>

      <Box p="sp24" pt={0}>
        <SectionHeading title={t("Manual")} aria-level={2} />

        <Flex px="sp12" py="sp16" direction="column" gap="sp16">
          <FormControl mb="10px" isDisabled>
            <Flex gap="sp8">
              <FormLabel width="110px" mt="sp12">
                {t("Latest Version")}
              </FormLabel>
              <Flex direction="column" width="80%">
                <Input placeholder={t("Latest version...")} value={newestVersion} isDisabled />
              </Flex>
            </Flex>
          </FormControl>
        </Flex>

        <Flex px="sp12" py="sp16" direction="row" gap="sp16">
          <Button
            mt="sp12"
            variant="primary"
            onClick={handleTriggerUpdate}
            isDisabled={saving}
            rightIcon={updateStatus === 1 ? <Spinner size="sm" color="white" /> : undefined}
          >
            {updateStatus === 1 ? <span>Updating</span> : <span>Update</span>}
          </Button>

          {(updateStatus === 2 || restartStatus === 1) && (
            <Button
              mt="sp12"
              variant="primary"
              onClick={handleTriggerRestart}
              isDisabled={saving}
              rightIcon={restartStatus === 1 ? <Spinner size="sm" color="white" /> : undefined}
            >
              {restartStatus === 1 ? <span>Restarting</span> : <span>Restart</span>}
            </Button>
          )}
        </Flex>

        {updateLog && updateStatus !== 0 && (
          <Flex px="sp12" py="sp16" direction="column" gap="sp16">
            <Flex direction="column" width="80%">
              <TextArea value={updateLog} rows={5} isDisabled label={t("Update Log")} />
            </Flex>
          </Flex>
        )}
      </Box>
      <Box p="sp24" backgroundColor="brand.100" borderBottomRadius="sm">
        <Button variant="primary" disabled={saving || !changes} onClick={handleSubmit}>
          {saving ? t("Saving..") : changes ? t("Save") : t("Saved")}
        </Button>
      </Box>
    </CardBasedPage>
  );
};
export default ManageDeviceUpdates;
